// @ts-nocheck
/**
 * The schedule board: a port of the original single-file Steel Strix board onto a live project.
 * All plan changes go through useProject's dispatch as PlanOps; this file only computes op
 * parameters (drop targets, ids) and renders.
 */
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useProject } from "../lib/useProject";
import { api } from "../lib/api";
import { buildModel, PriIcon, WS_COLORS, fmt, baseIdOf } from "./boardModel";
import { EditDialog, EDITOR_CSS, Hamburger, Plus, checkConfig } from "./Editors";
import { Avatar, DiscIcon, discMeta } from "../lib/disciplines";
import { BacklogView, BrickPanel, MOBILE_CSS, MobileNav, MobileTop, PersonPanel, useViewport, zoomLabel, ScreenLayer } from "./mobile";

const EMPTY_ENTRY = { free: [], pins: [], extra: [] };
const HISTORY_CAP = 50;
/** Zoom range in pixels per workday: from the whole project on one screen to a single day filling it. */
const MIN_PX = 0.25, MAX_PX = 400;
const clampPx = v => Math.min(MAX_PX, Math.max(MIN_PX, v));
const ROWS_MIN = 0.3, ROWS_MAX = 3;
const clampRows = v => Math.min(ROWS_MAX, Math.max(ROWS_MIN, v));
/* the desktop slider is logarithmic so the whole range stays usable */
const sliderOf = px => Math.round((Math.log(px / MIN_PX) / Math.log(MAX_PX / MIN_PX)) * 1000);
const pxOfSlider = v => MIN_PX * Math.pow(MAX_PX / MIN_PX, v / 1000);

export function Board({ projectId, toolbarLeft }: { projectId: string; toolbarLeft?: React.ReactNode }) {
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const errTimer = useRef<any>(null);
  const showError = useCallback((msg: string) => {
    setErrMsg(msg);
    clearTimeout(errTimer.current);
    errTimer.current = setTimeout(() => setErrMsg(null), 4000);
  }, []);
  useEffect(() => () => clearTimeout(errTimer.current), []);
  const live = useProject(projectId, showError);

  if (!live.project || !live.engine || !live.state || !live.config) {
    return (
      <div style={{ padding: 40, fontFamily: "system-ui", color: live.error ? "#e08a8a" : "#8A97A8", background: "#151A21", minHeight: "100vh", boxSizing: "border-box" }}>
        {toolbarLeft ? <div style={{ marginBottom: 16 }}>{toolbarLeft}</div> : null}
        {live.error ? `Couldn't load project: ${live.error}` : "Loading…"}
      </div>
    );
  }
  return <BoardView projectId={projectId} live={live} toolbarLeft={toolbarLeft} errMsg={errMsg} showError={showError} />;
}

// hovered / initiative-highlighted brick look; !important so it wins over the brick's inline resting style
const HOT_CSS = "background:var(--c)!important;color:#1a1d21!important;border:1px solid rgba(0,0,0,.4)!important;box-shadow:0 0 0 2px #E8A33D,0 0 16px rgba(232,163,61,.9)!important";

function BoardView({ projectId, live, toolbarLeft, errMsg, showError }) {
  const { project, config, engine, state, dispatch, saving, lastRemote, reload } = live;
  const m = useMemo(() => buildModel(config, engine), [config, engine]);
  const { PODS, P, PERSON_PODS, DPW, MW, START, RUNWAY_W, DOW, PRI, defU, priName, discLabel, labelOf, wsOwner, rate, laneOf, parseIntended, pinTracks, simPerson, computeMilestones, dayToDate } = m;

  const layout = state.layout;
  const pins = state.pins;
  const utilMap = state.util || {};
  const wsOrg = state.wsOrg || {};
  const uOf = nm => utilMap[nm] ?? defU(nm);

  const zoomKey = `sg-zoom-${projectId}`;
  const [pxPerDay, setPx] = useState(() => {
    try { const v = parseFloat(localStorage.getItem(zoomKey) || ""); if (v >= MIN_PX && v <= MAX_PX) return v; } catch (e) { /* storage unavailable */ }
    return 6;
  });
  useEffect(() => {
    // Lock the page while the board is up, and stop Safari's own page pinch everywhere on it.
    const stop = e => e.preventDefault();
    document.documentElement.classList.add("sg-lock");
    const multi = e => { if (e.touches.length > 1) e.preventDefault(); };
    const opts = { passive: false };
    document.addEventListener("gesturestart", stop, opts);
    document.addEventListener("gesturechange", stop, opts);
    document.addEventListener("touchmove", multi, opts);
    // If Safari still ends up zoomed in (double-tap, accessibility zoom gestures), snap back to 1:1:
    // rewriting the viewport tag makes iOS re-apply its scale.
    const vv = window.visualViewport, meta = document.querySelector('meta[name="viewport"]');
    const base = meta?.getAttribute("content") || "";
    let snapping = false;
    const unzoom = () => {
      if (!vv || !meta || snapping || vv.scale < 1.01) return;
      snapping = true;
      meta.setAttribute("content", base + ", minimum-scale=1");
      requestAnimationFrame(() => { meta.setAttribute("content", base); snapping = false; });
    };
    vv?.addEventListener("resize", unzoom);
    unzoom();
    return () => {
      vv?.removeEventListener("resize", unzoom);
      document.documentElement.classList.remove("sg-lock");
      document.removeEventListener("gesturestart", stop); document.removeEventListener("gesturechange", stop); document.removeEventListener("touchmove", multi);
    };
  }, []);
  useEffect(() => { try { localStorage.setItem(zoomKey, String(pxPerDay)); } catch (e) { /* storage unavailable */ } }, [zoomKey, pxPerDay]);

  const [drag, setDrag] = useState(null);
  const [hoverTarget, setHoverTarget] = useState(null); // {person, day, lane, isNew}
  const [pinPrompt, setPinPrompt] = useState(null);
  const [msDetail, setMsDetail] = useState(null);
  const [openWs, setOpenWs] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [highlightTask, setHighlightTask] = useState(null);
  const [flash, setFlash] = useState(null);
  const [scrollReq, setScrollReq] = useState(null);
  // Hover highlights are applied straight to the DOM (data attrs + CSS) instead of React state,
  // so hovering never re-renders the board and stays smooth with hundreds of bricks.
  const rootRef = useRef(null);
  const hovRef = useRef({ els: [] });
  const [flyoutFocus, setFlyoutFocus] = useState(null);
  const [edit, setEdit] = useState(null); // the open add/edit dialog
  const [dragWs, setDragWs] = useState(null);
  const [wsDrop, setWsDrop] = useState(null); // {pod, index}
  const [rowHover, setRowHover] = useState(null);
  const undoRef = useRef([]); const redoRef = useRef([]);
  const [, setHistN] = useState(0);
  const boardRef = useRef(null);
  const dayView = pxPerDay >= 14;
  const vp = useViewport();
  const mobile = vp.mobile, landscape = vp.landscape;
  const navigate = useNavigate();
  const [tab, setTab] = useState("board");
  const [panel, setPanel] = useState(null); // phone detail panel: {kind: "person", nm} | {kind: "brick", nm, segId}
  const [backlogOpen, setBacklogOpen] = useState(null);
  const hiddenKey = `sg-hidden-pods-${projectId}`;
  const [hiddenPods, setHiddenPods] = useState(() => { try { return JSON.parse(localStorage.getItem(hiddenKey) || "[]"); } catch (e) { return []; } });
  useEffect(() => { try { localStorage.setItem(hiddenKey, JSON.stringify(hiddenPods)); } catch (e) { /* storage unavailable */ } }, [hiddenKey, hiddenPods]);
  /* swim lanes: one row per person (grouped by team) or one row per initiative (grouped by owning team) */
  const lanesKey = `sg-lanes-${projectId}`;
  const [laneMode, setLaneMode] = useState(() => { try { return localStorage.getItem(lanesKey) === "initiatives" ? "initiatives" : "people"; } catch (e) { return "people"; } });
  useEffect(() => { try { localStorage.setItem(lanesKey, laneMode); } catch (e) { /* storage unavailable */ } }, [lanesKey, laneMode]);
  const byWs = laneMode === "initiatives";
  const RAIL = mobile ? 52 : 186;
  /* On phones the board scrolls with the page itself, so Safari can tuck its bars away; the top bar
     and time header stay pinned and the tab bar floats. In landscape the nav rail is pinned on the left. */
  const docMode = mobile && tab === "board";
  const NAV_W = "calc(62px + env(safe-area-inset-left))";
  const stickL = mobile && landscape ? NAV_W : 0;
  useEffect(() => { if (!docMode) window.scrollTo(0, 0); }, [docMode]);
  const hdrRef = useRef(null);
  const [hdrH, setHdrH] = useState(0);
  useLayoutEffect(() => {
    const el = hdrRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHdrH(el.offsetHeight));
    ro.observe(el);
    setHdrH(el.offsetHeight);
    return () => ro.disconnect();
  }, [mobile, docMode, landscape, tab]);
  /* semantic zoom: far out, bricks are thin bars; close in, they grow into cards */
  /* row height: a base per zoom level, scaled by the vertical pinch */
  const rowsKey = `sg-rows-${projectId}`;
  const [rowScale, setRowScale] = useState(() => { try { const v = parseFloat(localStorage.getItem(rowsKey) || ""); if (v >= ROWS_MIN && v <= ROWS_MAX) return v; } catch (e) { /* storage unavailable */ } return 1; });
  useEffect(() => { try { localStorage.setItem(rowsKey, String(rowScale)); } catch (e) { /* storage unavailable */ } }, [rowsKey, rowScale]);
  const baseRowH = pxPerDay < 1.2 ? 14 : pxPerDay >= 40 ? 58 : mobile ? 44 : 40;
  const rowH = Math.round(Math.min(160, Math.max(12, baseRowH * rowScale)));
  const thin = pxPerDay < 1.2 || rowH < 22;
  const detail = rowH >= 50 && pxPerDay >= 10;
  const smallAvatar = rowScale < 0.75;
  const minRowH = smallAvatar ? (mobile ? 30 : 26) : mobile ? 42 : 32; // room for the avatar when lanes are thin


  /* ---- "Updated by …" fades a few seconds after a remote change ---- */
  const [remoteVis, setRemoteVis] = useState(false);
  useEffect(() => {
    if (!lastRemote) return;
    setRemoteVis(true);
    const t = setTimeout(() => setRemoteVis(false), 4000);
    return () => clearTimeout(t);
  }, [lastRemote?.at]);

  /* ---- history: snapshots of the plan taken before each of our own dispatches ---- */
  const stateRef = useRef(state);
  stateRef.current = state;
  const takeSnap = () => {
    const s = stateRef.current;
    return JSON.parse(JSON.stringify({ layout: s.layout, pins: s.pins, util: s.util || {}, wsOrg: s.wsOrg || {}, backlog: s.backlog ?? null }));
  };
  /** Dispatches with an undo snapshot. Returns whether the op applied. */
  const act = (ops, { manual = false } = {}) => {
    const snap = takeSnap();
    if (!dispatch(ops)) return false;
    undoRef.current.push(snap);
    if (undoRef.current.length > HISTORY_CAP) undoRef.current.shift();
    redoRef.current = [];
    setHistN(n => n + 1);
    return true;
  };
  const restore = (s, reason) => dispatch({ type: "replaceState", layout: s.layout, pins: s.pins, util: s.util, wsOrg: s.wsOrg, backlog: s.backlog, reason });
  const doUndo = () => {
    if (!undoRef.current.length) return;
    const cur = takeSnap();
    const prev = undoRef.current.pop();
    if (restore(prev, "Undo")) redoRef.current.push(cur);
    else undoRef.current.push(prev);
    setHistN(n => n + 1);
  };
  const doRedo = () => {
    if (!redoRef.current.length) return;
    const cur = takeSnap();
    const nxt = redoRef.current.pop();
    if (restore(nxt, "Redo")) { undoRef.current.push(cur); if (undoRef.current.length > HISTORY_CAP) undoRef.current.shift(); }
    else redoRef.current.push(nxt);
    setHistN(n => n + 1);
  };

  const resolved = useMemo(() => {
    const r = {};
    for (const p of m.PEOPLE) r[p.name] = simPerson(p.name, layout[p.name] || EMPTY_ENTRY, utilMap[p.name] ?? defU(p.name));
    return r;
  }, [layout, state.util, engine]);

  const ovCount = nm => pinTracks(layout?.[nm]?.pins || [], nm, uOf(nm)).length;
  const laneCount = nm => 1 + ovCount(nm) + ((layout?.[nm]?.extra || []).length);

  const maxDay = useMemo(() => {
    let mx = RUNWAY_W * DPW;
    for (const segs of Object.values(resolved)) for (const s of segs) mx = Math.max(mx, s.d1);
    return Math.ceil(mx / DPW) * DPW;
  }, [resolved, RUNWAY_W, DPW]);
  const maxWeeks = maxDay / DPW;
  const milestones = useMemo(() => computeMilestones(resolved, maxWeeks), [resolved, maxWeeks]);
  /* every initiative: from bricks, the backlog (epics with no tasks yet) and the sidebar order (empty ones) */
  const initiatives = useMemo(() => {
    const all = new Set(Object.values(resolved).flat().map(s => s.ws.trim()));
    for (const t of state.backlog || []) all.add(t.w.trim());
    for (const list of Object.values(wsOrg)) for (const w of list) all.add(w.trim());
    return [...all].sort();
  }, [resolved, state.backlog, wsOrg]);
  const wsColor = useMemo(() => Object.fromEntries(initiatives.map((w, i) => [w, WS_COLORS[i % WS_COLORS.length]])), [initiatives]);
  const wsNum = useMemo(() => Object.fromEntries(initiatives.map((w, i) => [w, i])), [initiatives]);
  // Hover only flips classes on the few elements involved (no React render, no board-wide style
  // recalc) and only changes paint (colours/shadows, never z-index or opacity), so Chrome can take
  // its repaint-only fast path instead of re-layering the whole board on every hover.
  const setHov = (key, els, cls) => {
    for (const el of hovRef.current[key] || []) el.classList.remove(cls);
    for (const el of els) el.classList.add(cls);
    hovRef.current[key] = els;
  };
  const wsEls = (ws, sel) => {
    const root = rootRef.current, i = wsNum[ws];
    return root && i != null ? [...root.querySelectorAll(sel.replace(/#/g, String(i)))] : [];
  };
  const hoverBrick = (ws, task, id, el) => {
    if (ws == null) { setHov("sib", [], "sib"); setHov("chip", [], "sib"); setHov("fly", [], "hv"); return; }
    setHov("sib", wsEls(ws, '.brk[data-ws="#"]').filter(b => b !== el), "sib");
    setHov("chip", wsEls(ws, '.wschip[data-wsi="#"]'), "sib");
    setHov("fly", [...rootRef.current.querySelectorAll(`[data-flyrow="${CSS.escape(id)}"], [data-flyep="${CSS.escape(task)}"]`)], "hv");
  };
  const hoverWs = ws => setHov("fhot", ws == null ? [] : wsEls(ws, '.brk[data-ws="#"]'), "fhot");
  const wsIndex = useMemo(() => {
    const idx = {};
    for (const [nm, segs] of Object.entries(resolved)) {
      for (const s of segs) {
        const w = s.ws.trim();
        idx[w] = idx[w] || {};
        idx[w][s.task] = idx[w][s.task] || [];
        const baseId = baseIdOf(s.id);
        const ex = idx[w][s.task].find(e => e.segId === baseId);
        if (ex) { ex.pd += s.pd; ex.startDay = Math.min(ex.startDay, s.d0); }
        else idx[w][s.task].push({ segId: baseId, disc: s.disc, person: nm, pd: s.pd, startDay: s.d0, key: s.key, pri: s.pri, isSub: !!s.isSub, parent: s.parent, pinned: !!s.pinned });
      }
    }
    for (const w of Object.keys(idx)) for (const t of Object.keys(idx[w])) {
      const byDisc = {};
      for (const e of idx[w][t]) (byDisc[e.disc] = byDisc[e.disc] || []).push(e);
      for (const list of Object.values(byDisc)) {
        if (list.length > 1) {
          list.sort((a, b) => (a.isSub ? 1 : 0) - (b.isSub ? 1 : 0) || a.startDay - b.startDay);
          list.forEach((e, i) => { e.num = i + 1; });
        }
      }
    }
    return idx;
  }, [resolved]);

  /* ---- chunk ops ---- */
  const addChunk = segId => act({ type: "splitChunk", segId, newId: `sub-${crypto.randomUUID().slice(0, 8)}` }, { manual: true });
  const bumpChunk = (segId, delta) => act({ type: "resizeChunk", segId, delta }, { manual: true });
  const delChunk = segId => act({ type: "deleteChunk", segId }, { manual: true });

  /* sidebar org: stored order merged with live workstreams; new ones join their owner pod */
  const displayOrg = useMemo(() => {
    const live = new Set(initiatives);
    const org = {};
    for (const pod of PODS) org[pod.id] = (wsOrg[pod.id] || []).filter(w => live.has(w));
    const placed = new Set(Object.values(org).flat());
    const fallback = PODS[0]?.id;
    for (const w of [...live].sort()) if (!placed.has(w)) {
      const own = wsOwner(w);
      const target = own in org ? own : fallback;
      if (target !== undefined) org[target].push(w);
    }
    return org;
  }, [wsOrg, initiatives, PODS]);
  /* initiative mode: every brick of an initiative, packed greedily into as few lanes as fit without overlap */
  const wsLanes = useMemo(() => {
    if (!byWs) return {};
    const out = {};
    for (const [nm, segs] of Object.entries(resolved)) for (const s of segs) (out[s.ws.trim()] ||= { items: [], lanes: 0 }).items.push({ nm, s });
    for (const L of Object.values(out)) {
      const ends = [];
      L.items.sort((a, b) => a.s.d0 - b.s.d0 || a.s.d1 - b.s.d1);
      for (const it of L.items) {
        let l = ends.findIndex(e => e <= it.s.d0 + 1e-6);
        if (l < 0) { l = ends.length; ends.push(0); }
        ends[l] = it.s.d1;
        it.lane = l;
      }
      L.lanes = ends.length;
    }
    return out;
  }, [byWs, resolved]);
  const wsOfTeam = w => +(Object.entries(displayOrg).find(([, list]) => list.includes(w))?.[0] ?? wsOwner(w));

  /* the flyout lists the open initiative's epics from the backlog, with their bricks grouped as tasks */
  const flyEpics = useMemo(() => {
    if (!openWs) return [];
    const byName = new Map();
    for (const t of state.backlog || []) if (t.w.trim() === openWs) byName.set(t.n, { name: t.n, pri: t.p, inScope: t.s, item: t, entries: [] });
    for (const [task, entries] of Object.entries(wsIndex[openWs] || {})) {
      const e = byName.get(task) || { name: task, pri: entries[0].pri, inScope: true, item: null, entries: [] };
      e.entries = entries;
      byName.set(task, e);
    }
    const start = e => (e.entries.length ? Math.min(...e.entries.map(x => x.startDay)) : Infinity);
    return [...byName.values()].sort((a, b) => (PRI[a.pri] ?? 9) - (PRI[b.pri] ?? 9) || start(a) - start(b) || a.name.localeCompare(b.name));
  }, [openWs, state.backlog, wsIndex, PRI]);

  /* teams and people are project settings; save the whole config and reload */
  const saveConfig = async next => {
    await api.putConfig(projectId, checkConfig(next));
    await reload();
  };
  const wsOrgRef = useRef({});
  wsOrgRef.current = displayOrg;

  /* sidebar chip drag: reorder within a pod group or re-home to another (visibility only) */
  useEffect(() => {
    if (!dragWs) return;
    const move = e => {
      const activeNow = dragWs.active || Math.hypot(e.clientX - dragWs.x0, e.clientY - dragWs.y0) > 4;
      setDragWs(d => ({ ...d, x: e.clientX, y: e.clientY, active: activeNow }));
      if (!activeNow) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const chip = el?.closest?.("[data-wschip]");
      if (chip) {
        const pod = +chip.dataset.pod;
        const idx = +chip.dataset.idx;
        const r = chip.getBoundingClientRect();
        setWsDrop({ pod, index: e.clientY > r.top + r.height / 2 ? idx + 1 : idx });
        return;
      }
      const zone = el?.closest?.("[data-podzone]");
      if (zone) { const pod = +zone.dataset.podzone; setWsDrop({ pod, index: (wsOrgRef.current[pod] || []).length }); return; }
      setWsDrop(null);
    };
    const up = () => {
      if (dragWs.active && wsDrop) {
        const org = JSON.parse(JSON.stringify(wsOrgRef.current));
        const fromEntry = Object.entries(org).find(([, list]) => list.includes(dragWs.ws));
        if (fromEntry) {
          const fromPod = +fromEntry[0];
          const fromIdx = org[fromPod].indexOf(dragWs.ws);
          org[fromPod].splice(fromIdx, 1);
          let insertAt = wsDrop.index;
          if (wsDrop.pod === fromPod && fromIdx < insertAt) insertAt--;
          (org[wsDrop.pod] = org[wsDrop.pod] || []).splice(insertAt, 0, dragWs.ws);
          act({ type: "setWsOrg", org });
        }
      } else if (!dragWs.active) {
        setOpenWs(o => (o === dragWs.ws ? null : dragWs.ws)); setHighlight(null);
      }
      setDragWs(null); setWsDrop(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [dragWs, wsDrop]);

  const setPersonUtil = (nm, v) => {
    const u = Math.min(1, Math.max(0.1, v));
    act({ type: "setUtil", person: nm, value: u });
  };

  const togglePin = (nm, seg) => {
    if (act({ type: "togglePin", person: nm, segId: baseIdOf(seg.id) })) setPinPrompt(null);
  };

  /* ---- drag & drop ---- */
  const tapBrick = (nm, seg) => {
    if (mobile) setPanel({ kind: "brick", nm, segId: baseIdOf(seg.id) });
    else { setOpenWs(seg.ws.trim()); setFlyoutFocus(baseIdOf(seg.id)); }
  };
  /* touch: a tap opens the brick; press and hold lifts it to drag, so a swipe still scrolls the board */
  /* fingers currently down, so a pinch never turns into a brick tap or a long-press drag */
  const touchesRef = useRef(new Set());
  useEffect(() => {
    const down = e => { if (e.pointerType === "touch") touchesRef.current.add(e.pointerId); };
    const up = e => touchesRef.current.delete(e.pointerId);
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", up, true);
    return () => { window.removeEventListener("pointerdown", down, true); window.removeEventListener("pointerup", up, true); window.removeEventListener("pointercancel", up, true); };
  }, []);
  const beginDrag = (e, nm, seg) => {
    if (seg.ghost) return;
    if (e.pointerType === "touch") {
      if (touchesRef.current.size > 1) return; // the second finger of a pinch
      const id = e.pointerId, x0 = e.clientX, y0 = e.clientY;
      let moved = false;
      const done = () => {
        clearTimeout(timer);
        window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", done);
        window.removeEventListener("pointerdown", second);
      };
      const timer = setTimeout(() => {
        done();
        if (touchesRef.current.size > 1) return;
        navigator.vibrate?.(12);
        setDrag({ from: nm, seg, x: x0, y: y0, x0, y0, active: true, touch: true });
      }, 380);
      const mv = ev => { if (ev.pointerId === id && Math.hypot(ev.clientX - x0, ev.clientY - y0) > 8) { moved = true; done(); } };
      const up = ev => { if (ev.pointerId !== id) return; done(); if (!moved) tapBrick(nm, seg); };
      const second = ev => { if (ev.pointerId !== id) { moved = true; done(); } }; // a second finger means a pinch, not a hold or tap
      window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", done);
      window.addEventListener("pointerdown", second);
      return;
    }
    e.preventDefault();
    setDrag({ from: nm, seg, x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, active: false });
  };
  useEffect(() => {
    if (!drag) return;
    const move = e => {
      const activeNow = drag.active || Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) > 4;
      setDrag(d => ({ ...d, x: e.clientX, y: e.clientY, active: activeNow }));
      if (!activeNow) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const rowEl = el?.closest?.("[data-person]");
      if (rowEl) {
        const nm = rowEl.dataset.person;
        if (P[nm] && P[nm].disc === drag.seg.disc) {
          const rect = rowEl.getBoundingClientRect();
          const day = Math.max(0, (e.clientX - rect.left - RAIL) / pxPerDay);
          const lanes = laneCount(nm);
          const relY = e.clientY - rect.top;
          let lane = Math.floor(relY / rowH);
          const inPreviewStrip = relY > lanes * rowH;
          if (inPreviewStrip) lane = lanes;
          lane = Math.min(lane, lanes);
          setHoverTarget({ person: nm, day, lane, isNew: lane === lanes });
          return;
        }
      }
      setHoverTarget(null);
    };
    const up = () => {
      if (!drag.active) {
        tapBrick(drag.from, drag.seg);
        setDrag(null); setHoverTarget(null);
        return;
      }
      if (hoverTarget) {
        const { person, day, lane } = hoverTarget;
        const baseId = baseIdOf(drag.seg.id);
        const toFlowLane = lane <= ovCount(person); // pin-track drops route to the flow lane
        const ok = act({ type: "move", segId: baseId, from: drag.from, to: person, lane, day }, { manual: true });
        if (ok && toFlowLane) {
          setPinPrompt({ person, seg: { ...drag.seg, id: baseId }, until: Date.now() + 2500 });
          setTimeout(() => setPinPrompt(p => (p && Date.now() >= p.until ? null : p)), 2600);
        }
      }
      setDrag(null); setHoverTarget(null);
    };
    if (drag.touch) {
      /* a held touch drags with touch events, which (unlike pointer events) can stop the page from scrolling */
      const tmove = ev => { ev.preventDefault(); const t = ev.touches[0]; if (t) move(t); };
      const tend = () => up();
      window.addEventListener("touchmove", tmove, { passive: false });
      window.addEventListener("touchend", tend);
      window.addEventListener("touchcancel", tend);
      return () => { window.removeEventListener("touchmove", tmove); window.removeEventListener("touchend", tend); window.removeEventListener("touchcancel", tend); };
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [drag, hoverTarget, state, pxPerDay]);

  /* ---- pinch zoom (touch) and ctrl/trackpad-pinch wheel zoom, anchored on the day under the fingers ---- */
  const pxRef = useRef(pxPerDay);
  pxRef.current = pxPerDay;
  const anchorRef = useRef(null);
  const rafRef = useRef(0);
  const rowScaleRef = useRef(rowScale);
  rowScaleRef.current = rowScale;
  /** Zooms time to `px` keeping `day` under client offset `cx`; optionally scales rows keeping a row point under the fingers. */
  const zoomTo = (px, day, cx, rows = null) => {
    anchorRef.current = { day, cx, px: clampPx(px), left0: boardRef.current?.getBoundingClientRect().left ?? 0,
      rs: rows ? clampRows(rows.scale) : rowScaleRef.current, row: rows?.row ?? null };
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; setPx(anchorRef.current.px); setRowScale(anchorRef.current.rs); });
  };
  useLayoutEffect(() => {
    const a = anchorRef.current, el = boardRef.current;
    if (!a || !el || Math.abs(a.px - pxPerDay) > 1e-9 || Math.abs(a.rs - rowScale) > 1e-9) return;
    if (docMode) window.scrollBy(el.getBoundingClientRect().left + RAIL + a.day * pxPerDay - (a.left0 + a.cx), 0);
    else el.scrollLeft = Math.max(0, RAIL + a.day * pxPerDay - a.cx);
    if (a.row?.el.isConnected) {
      const r = a.row.el.getBoundingClientRect();
      const delta = r.top + a.row.frac * r.height - a.row.y;
      if (docMode) window.scrollBy(0, delta); else el.scrollTop += delta;
    }
    anchorRef.current = null;
  }, [pxPerDay, rowScale]);
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    let pinch = null;
    const mid = t => (t[0].clientX + t[1].clientX) / 2 - el.getBoundingClientRect().left;
    const dist = t => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY) || 1;
    /* the pinch splits by axis: spreading sideways stretches time, spreading up-down grows the rows */
    const span = t => [Math.abs(t[0].clientX - t[1].clientX), Math.abs(t[0].clientY - t[1].clientY)];
    const SOFT = 40; // keeps a near-zero span from exploding the ratio
    const ts = e => {
      if (e.touches.length !== 2) return;
      const cx = mid(e.touches);
      const [dx0, dy0] = span(e.touches);
      const y = (e.touches[0].clientY + e.touches[1].clientY) / 2, x = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const rowEl = document.elementFromPoint(x, y)?.closest("[data-person]");
      const r = rowEl?.getBoundingClientRect();
      pinch = {
        dx0, dy0, px0: pxRef.current, rs0: rowScaleRef.current, day: (el.scrollLeft + cx - RAIL) / pxRef.current,
        axis: dx0 > 2.5 * dy0 ? "x" : dy0 > 2.5 * dx0 ? "y" : "xy",
        row: rowEl ? { el: rowEl, frac: (y - r.top) / Math.max(1, r.height), y } : null,
      };
      el.classList.add("sg-pinching");
    };
    const tm = e => {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const [dx, dy] = span(e.touches);
      const sx = pinch.axis === "y" ? 1 : (dx + SOFT) / (pinch.dx0 + SOFT);
      const sy = pinch.axis === "x" ? 1 : (dy + SOFT) / (pinch.dy0 + SOFT);
      zoomTo(pinch.px0 * sx, pinch.day, mid(e.touches), { scale: pinch.rs0 * sy, row: pinch.row });
    };
    const te = e => { if (e.touches.length < 2) { pinch = null; el.classList.remove("sg-pinching"); } };
    const wheel = e => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const cx = e.clientX - el.getBoundingClientRect().left;
      const base = anchorRef.current?.px ?? pxRef.current;
      zoomTo(base * Math.exp(-e.deltaY * 0.01), (el.scrollLeft + cx - RAIL) / pxRef.current, cx);
    };
    const gesture = e => e.preventDefault(); // Safari's own page pinch
    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchmove", tm, { passive: false });
    el.addEventListener("touchend", te);
    el.addEventListener("touchcancel", te);
    el.addEventListener("wheel", wheel, { passive: false });
    el.addEventListener("gesturestart", gesture);
    return () => {
      el.removeEventListener("touchstart", ts); el.removeEventListener("touchmove", tm); el.removeEventListener("touchend", te);
      el.removeEventListener("touchcancel", te); el.removeEventListener("wheel", wheel); el.removeEventListener("gesturestart", gesture);
    };
  }, [RAIL, tab, mobile, docMode]);

  /* ---- keyboard ---- */
  useEffect(() => {
    const onKey = e => {
      if (e.target && /TEXTAREA|INPUT/.test(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? doRedo() : doUndo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); doRedo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (!scrollReq) return;
    const t = setTimeout(() => {
      const el = boardRef.current?.querySelector(`[data-segkey="${CSS.escape(scrollReq)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        setFlash(scrollReq);
        setTimeout(() => setFlash(f => (f === scrollReq ? null : f)), 2400);
      }
      setScrollReq(null);
    }, 60);
    return () => clearTimeout(t);
  }, [scrollReq, pxPerDay]);
  useEffect(() => {
    if (!openWs || !flyoutFocus) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-flyrow="${CSS.escape(flyoutFocus)}"]`);
      if (el) el.scrollIntoView({ block: "nearest" });
      setTimeout(() => setFlyoutFocus(f => (f === flyoutFocus ? null : f)), 2500);
    }, 80);
    return () => clearTimeout(t);
  }, [openWs, flyoutFocus]);

  const weekPx = pxPerDay * DPW;

  /* NOW line: today's position in workdays since project start */
  const nowDay = useMemo(() => {
    if (Date.now() < START.getTime()) return 0;
    return engine.dateToDay(new Date()) + (new Date().getHours() >= 12 ? 0.5 : 0);
  }, [engine, START]);

  /* ---- gridline background (week solid, mid-week fainter, days dotted when zoomed) ---- */
  const gridBg = useMemo(() => {
    const mid = Math.floor(DPW / 2);
    if (weekPx < 10) return `repeating-linear-gradient(90deg, #2E3947 0 1px, transparent 1px ${weekPx * MW}px)`;
    const layers = [
      `repeating-linear-gradient(90deg, #2E3947 0 1px, transparent 1px ${weekPx}px)`,
      `repeating-linear-gradient(90deg, transparent 0 ${mid * pxPerDay}px, #232B36 ${mid * pxPerDay}px ${mid * pxPerDay + 1}px, transparent ${mid * pxPerDay + 1}px ${weekPx}px)`,
    ];
    if (pxPerDay >= 24) {
      const svg = encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='${pxPerDay}' height='8'><rect x='0' y='0' width='1' height='4' fill='%23212a35'/></svg>`);
      layers.push(`url("data:image/svg+xml,${svg}")`);
    }
    return layers.join(", ");
  }, [pxPerDay, weekPx, DPW, MW]);

  const renderSegs = (nm, podId) => (resolved[nm] || []).map(s => renderBrick(nm, s, s.lane, podId, false));
  /** One brick at lane `laneIdx` of its row. In initiative rows (`inWs`) it carries the person's avatar and opens on tap instead of dragging. */
  const renderBrick = (nm, s, laneIdx, podId, inWs) => {
    {
      const podLane = laneOf(nm, s.ws);
      const isGhost = podId !== undefined && podLane !== podId && (PERSON_PODS[nm] || []).length > 1;
      const left = s.d0 * pxPerDay, width = Math.max(3, (s.d1 - s.d0) * pxPerDay - 1);
      const bg = wsColor[s.ws.trim()] || "#D3D0D0";
      const inten = s.intended ? parseIntended(s.intended, s.disc) : { names: [] };
      const misI = inten.names.length && !inten.names.includes(nm);
      const baseId = baseIdOf(s.id);
      const hot = !isGhost && (highlight === baseId || flash === baseId || (highlightTask && highlightTask === s.task));
      const avgFocus = s.d1 > s.d0 ? Math.min(1, s.pd / (rate(nm, uOf(nm)) * (s.d1 - s.d0))) : 1;
      const shared = avgFocus < 0.99 && s.prof && s.prof.some(p => p[2] < 0.999);
      const isDragSrc = drag && !isGhost && nm === drag.from && baseId === baseIdOf(drag.seg.id);
      let previewShift = 0;
      if (drag?.active && hoverTarget && hoverTarget.person === nm && s.lane === 0 && hoverTarget.lane <= ovCount(nm) && !isGhost && !s.pinned && !isDragSrc) {
        if ((s.d0 + s.d1) / 2 >= hoverTarget.day) previewShift = (drag.seg.pd / rate(nm, uOf(nm))) * pxPerDay;
      }
      const avSize = Math.max(14, Math.min(detail ? 30 : 24, rowH - 12));
      return (
        <div key={(inWs ? nm + "\u0000" : "") + s.id + "L" + s.lane + (isGhost ? "g" : "")}
          className={isGhost ? undefined : thin ? "brk thin" : "brk"}
          data-ws={isGhost ? undefined : wsNum[s.ws.trim()]}
          data-segkey={isGhost ? undefined : baseId}
          onPointerDown={isGhost || inWs ? undefined : e => beginDrag(e, nm, s)}
          onClick={inWs ? () => tapBrick(nm, s) : undefined}
          onMouseEnter={drag ? undefined : isGhost ? () => setHighlight(baseId) : e => hoverBrick(s.ws.trim(), s.task, baseId, e.currentTarget)}
          onMouseLeave={isGhost ? () => setHighlight(h => (h === baseId ? null : h)) : () => hoverBrick(null)}
          title={`${inWs ? `${P[nm]?.full ?? nm} · ` : ""}${priName(s.pri)}${s.isN ? " (out of budget)" : ""} · ${s.task}\n${s.ws} · ${discLabel(s.disc)} · ${s.pd.toFixed(1)}pd${shared ? ` · ${Math.round(avgFocus * 100)}% focus` : ""}${s.lane > 0 ? ` · lane ${s.lane + 1}${s.anchorDay != null ? " (floats)" : ""}` : ""}\n${fmt(dayToDate(s.d0))} → ${fmt(dayToDate(Math.max(s.d0, s.d1 - 0.01)))}${s.pinned ? "\n📌 pinned" : ""}${misI ? "\n⚠ not the intended dev" : ""}`}
          style={{
            position: "absolute", left, width, top: thin ? laneIdx * rowH + (rowH - 10) / 2 : 4 + laneIdx * rowH, height: thin ? 10 : rowH - 9,
            "--bar": hot ? "#1a1d2120" : bg + "1E", "--c": bg, "--cs": bg + "38", "--ca": bg + "aa", "--cg": bg + "66",
            background: isGhost ? "transparent" : hot || thin ? bg : "#20262F",
            border: thin && !isGhost ? "none" : s.isN ? `1.5px dashed ${bg}66` : isGhost ? `1.5px dashed ${bg}` : hot ? "1px solid rgba(0,0,0,.4)" : `1px solid ${bg}55`,
            opacity: isGhost ? 0.45 : s.isN && !hot ? 0.85 : 1,
            borderRadius: 3, cursor: isGhost ? "default" : inWs ? "pointer" : "grab",
            overflow: "hidden", fontSize: detail ? 12 : mobile ? 11 : 10, lineHeight: detail ? "14px" : mobile ? "12px" : "11px", padding: thin ? 0 : detail ? "4px 6px" : "2px 3px", boxSizing: "border-box",
            color: isGhost ? bg : hot ? "#1a1d21" : bg, fontWeight: 600, userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none",
            boxShadow: hot ? "0 0 0 2px #E8A33D, 0 0 16px rgba(232,163,61,.9)" : "none",
            zIndex: hot ? 5 : 1,
            animation: flash === baseId && !isGhost ? "ssxPulse 0.6s ease-in-out 3" : "none",
            transform: previewShift ? `translateX(${previewShift}px)` : "none",
            filter: isDragSrc && drag?.active ? "opacity(0.3)" : "none",
            transition: "left .25s ease, top .25s ease, transform .18s ease",
            display: "flex", alignItems: inWs && !detail ? "center" : "flex-start", gap: inWs ? 4 : 2,
          }}>
          {inWs && !thin && width > avSize + 4 && P[nm] && <span style={{ position: "relative", zIndex: 2, display: "flex", flexShrink: 0 }}>
            <Avatar name={P[nm].full} disc={discMeta(config, P[nm].disc)} size={avSize} badge={avSize >= 22} title={`${P[nm].full} · ${discLabel(P[nm].disc)}`} />
          </span>}
          {pxPerDay >= 3 && !thin && !isGhost && s.prof && (() => {
            // one striped div per run of equal-focus days (not one div per day): far fewer elements to hit-test and paint
            const bars = [], stripe = `repeating-linear-gradient(90deg, var(--bar) 0 ${Math.max(1, pxPerDay - 1)}px, transparent 0 ${pxPerDay}px)`;
            let run = null;
            for (let dd = Math.floor(s.d0); dd < s.d1; dd++) {
              const mid = Math.min(Math.max(dd + 0.5, s.d0), s.d1 - 0.001);
              const iv = s.prof.find(p => mid >= p[0] - 1e-6 && mid <= p[1] + 1e-6);
              const h = Math.round((iv ? iv[2] : 1) * 100);
              if (run && run.h === h) run.n++; else bars.push(run = { dd, h, n: 1 });
            }
            const barEls = bars.map(r => <div key={r.dd} style={{ position: "absolute", bottom: 0, left: (r.dd - s.d0) * pxPerDay, width: r.n * pxPerDay, height: `${r.h}%`, background: stripe }} />);
            return <div className="bars" style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>{barEls}</div>;
          })()}
          {!thin && s.pinned && <span style={{ fontSize: 9, cursor: "pointer", zIndex: 2 }} title="unpin" onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); togglePin(nm, s); }}>📌</span>}
          {!thin && !s.pinned && !isGhost && !mobile && !inWs &&
            <span className="pinhint" style={{ fontSize: 9, cursor: "pointer", opacity: 0.35, zIndex: 2 }} title="pin (stays put when other work shifts)"
              onPointerDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); togglePin(nm, s); }}>📌</span>}
          {s.orphan && <span title="task no longer in backlog" style={{ color: "#c0392b", fontSize: 9 }}>⚑</span>}
          {misI ? <span style={{ fontSize: 9 }}>⚠</span> : null}
          {!thin && width > 24 && <span style={{ marginTop: detail ? 2 : 1 }}><PriIcon p={s.pri} size={detail ? 11 : 9} model={m} /></span>}
          {detail
            ? <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, position: "relative" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <DiscIcon icon={discMeta(config, s.disc).icon} size={13} strokeWidth={2.4} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{s.task}</span>
                </span>
                <span style={{ fontSize: 10.5, fontWeight: 500, opacity: .75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {discLabel(s.disc)} {s.pd.toFixed(1)}pd · {inWs ? labelOf(nm) : s.ws.trim()} · {fmt(dayToDate(s.d0))} → {fmt(dayToDate(Math.max(s.d0, s.d1 - 0.01)))}
                </span>
              </span>
            : !thin && <span style={rowH < 36 || inWs ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 } : undefined}>{width > 42 + (inWs ? avSize : 0) ? s.task : ""}</span>}
        </div>
      );
    }
  };

  const teamHeader = pod => (
    <div style={{ display: "flex", alignItems: "center", background: pod.color + "26", borderLeft: `4px solid ${pod.color}`, borderBottom: "1px solid #2E3947" }}>
      <div className="hov" style={{ width: mobile ? "auto" : RAIL, maxWidth: mobile ? (landscape ? `calc(100vw - ${NAV_W})` : "100vw") : undefined, boxSizing: "border-box", padding: "4px 4px 4px 8px", font: "700 12px 'Barlow Condensed'", letterSpacing: ".07em", color: pod.color, position: "sticky", left: stickL, background: "#151A21", borderRight: "1px solid #2E3947", whiteSpace: "nowrap", overflow: "hidden", display: "flex", alignItems: "center", zIndex: 11 }}>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{pod.name}</span>
        <Hamburger title={`Edit team ${pod.name}`} onClick={() => setEdit({ kind: "team", mode: "edit", team: pod.id })} />
      </div>
      <div style={{ flex: 1, fontSize: 10, color: "#8A97A8", padding: "0 8px", whiteSpace: "nowrap", overflow: "hidden" }}>
        {!mobile && (() => { const s = milestones[pod.idx]?.[0]; return s && (s.done.length || s.wip.length) ? `M1 → ${s.done.length} done · ${s.wip.length} in flight (click month headers)` : ""; })()}
      </div>
    </div>
  );

  const canUndo = undoRef.current.length > 0, canRedo = redoRef.current.length > 0;
  const onTab = k => {
    if (k === "team") navigate("/team");
    else if (k === "settings") navigate(`/p/${projectId}/settings`);
    else { setTab(k); setPanel(null); }
  };

  return (
    <div ref={rootRef} className={drag ? "sg-board sg-dragging" : "sg-board"} style={{ fontFamily: "'Barlow Semi Condensed','Segoe UI',system-ui,sans-serif", background: "#151A21", color: "#E8EDF3", ...(docMode
      ? { minHeight: "100lvh", width: "max-content", minWidth: "100%", paddingLeft: stickL, paddingBottom: landscape ? 0 : "calc(64px + env(safe-area-inset-bottom))" }
      : { height: "100dvh", width: "100%", overflow: "hidden" }), display: "flex", flexDirection: "column" }}>
      <style>{`
        .sg-board *,.sg-board *::before,.sg-board *::after{box-sizing:border-box}
        ::-webkit-scrollbar{height:10px;width:10px}::-webkit-scrollbar-thumb{background:#2E3947;border-radius:5px}::-webkit-scrollbar-track{background:#151A21}
        .btn{background:#232B36;border:1px solid #364356;color:#E8EDF3;border-radius:4px;padding:5px 12px;font:600 12px 'Barlow Condensed';letter-spacing:.06em;text-transform:uppercase;cursor:pointer}
        .btn:hover{background:#2C3644}.btn:focus-visible{outline:2px solid #E8A33D}
        .sg-pinching, .sg-pinching *{transition:none!important}
        @keyframes ssxPulse{0%,100%{filter:brightness(1)}50%{filter:brightness(1.6)}}
        .wschip{border:none;border-radius:3px;padding:4px 10px;font:600 11px 'Barlow Condensed';letter-spacing:.05em;cursor:pointer;color:#1a1d21;white-space:nowrap}
        .wschip:hover{filter:brightness(1.12)}
        .brk .pinhint{display:none}
        .sg-board:not(.sg-dragging) .brk:hover .pinhint{display:inline}
        .sg-board:not(.sg-dragging) .brk:hover,.brk.fhot{${HOT_CSS}}
        .sg-board:not(.sg-dragging) .brk:hover,.brk.fhot{--bar:#1a1d2120!important}
        .sg-board:not(.sg-dragging) .brk.sib{border-color:var(--c)!important;box-shadow:0 0 0 1px var(--ca),0 0 6px var(--cg)!important}
        .sg-board:not(.sg-dragging) .brk.sib:not(.thin){background:var(--cs)!important}
        .wschip.sib{outline:2px solid #E8EDF3;box-shadow:0 0 10px rgba(232,237,243,.5)}
        .flyrow.hv{background:#3A2F17;outline:1px solid #E8A33D}
        .flyep.hv{color:#E8A33D!important}
        .flyrow{display:flex;gap:8px;align-items:center;padding:3px 8px;border-radius:3px;cursor:pointer;font-size:12px}
        .flyrow:hover{background:#2C3644}
        ${EDITOR_CSS}
        ${MOBILE_CSS}
        @media (prefers-reduced-motion: reduce){*{transition:none!important;animation:none!important}}
      `}</style>

      {/* toolbar */}
      {mobile ? <div style={docMode ? { position: "sticky", left: stickL, width: landscape ? `calc(100vw - ${NAV_W})` : "100vw", zIndex: 40, background: "#151A21" } : undefined}><MobileTop project={project} onBack={() => navigate("/")} onUndo={doUndo} canUndo={canUndo} pods={PODS} hidden={hiddenPods}
        togglePod={id => setHiddenPods(h => (h.includes(id) ? h.filter(x => x !== id) : [...h, id]))} px={pxPerDay} landscape={landscape} tab={tab}
        laneMode={laneMode} toggleLanes={() => setLaneMode(v => (v === "initiatives" ? "people" : "initiatives"))}
        status={errMsg ? <span style={{ fontSize: 12, color: "#e05a4f", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{errMsg}</span>
          : saving ? <span style={{ fontSize: 12, color: "#8A97A8" }}>Saving…</span> : null} /></div>
      : <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderBottom: "1px solid #2E3947", flexWrap: "wrap", background: "#151A21", zIndex: 30 }}>
        {toolbarLeft}
        <div style={{ font: "700 20px 'Barlow Condensed'", letterSpacing: ".08em", color: "#E8A33D", textTransform: "uppercase" }}>{project.name}</div>
        <div style={{ flex: 1 }} />
        <div role="group" aria-label="Swim lanes" style={{ display: "flex" }}>
          {[["people", "People"], ["initiatives", "Initiatives"]].map(([k, l], i) => (
            <button key={k} className="btn" onClick={() => setLaneMode(k)} aria-pressed={laneMode === k}
              style={{ borderRadius: i ? "0 4px 4px 0" : "4px 0 0 4px", marginLeft: i ? -1 : 0, ...(laneMode === k ? { background: "#3A2F17", borderColor: "#E8A33D", color: "#E8A33D", position: "relative" } : { color: "#8A97A8" }) }}>{l}</button>
          ))}
        </div>
        <label style={{ fontSize: 11, color: "#8A97A8", display: "flex", alignItems: "center", gap: 6 }}>
          PROJECT
          <input type="range" min={0} max={1000} step={1} value={sliderOf(pxPerDay)}
            onChange={e => setPx(clampPx(pxOfSlider(+e.target.value)))}
            style={{ width: 170, accentColor: "#E8A33D" }} aria-label="Zoom" title="Zoom (or pinch / Ctrl+scroll on the board)" />
          DAY
        </label>
        <button className="btn" onClick={doUndo} disabled={!canUndo} style={{ opacity: canUndo ? 1 : 0.35 }} title="Ctrl/Cmd+Z">↶</button>
        <button className="btn" onClick={doRedo} disabled={!canRedo} style={{ opacity: canRedo ? 1 : 0.35 }} title="Ctrl/Cmd+Shift+Z">↷</button>
        {errMsg && <span style={{ fontSize: 12, color: "#e05a4f" }}>{errMsg}</span>}
        {saving && <span style={{ fontSize: 12, color: "#8A97A8" }}>Saving…</span>}
        {lastRemote && <span style={{ fontSize: 12, color: "#5FD3BC", opacity: remoteVis ? 1 : 0, transition: "opacity 1s", pointerEvents: "none" }}>Updated by {lastRemote.actorName || "someone"}</span>}
      </div>}

      {/* main row: workstream sidebar | flyout panel | board */}
      <div style={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
        {mobile && landscape && (docMode
          ? <ScreenLayer z={45}><div style={{ position: "fixed", top: 0, bottom: 0, left: 0, display: "flex" }}><MobileNav tab={tab} onTab={onTab} vertical /></div></ScreenLayer>
          : <div style={{ display: "flex" }}><MobileNav tab={tab} onTab={onTab} vertical /></div>)}
        {mobile && tab === "backlog" && <BacklogView config={config} state={state} m={m} pods={PODS} displayOrg={displayOrg} wsColor={wsColor} wsIndex={wsIndex}
          resolved={resolved} maxDay={maxDay} nowDay={nowDay} wsOfTeam={wsOfTeam} landscape={landscape} open={backlogOpen} setOpen={setBacklogOpen} setEdit={setEdit} />}
        {!mobile && <div style={{ width: 150, flexShrink: 0, borderRight: "1px solid #2E3947", background: "#181F28", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ font: "600 10px 'Barlow Condensed'", letterSpacing: ".1em", color: "#5F6B7C", padding: "8px 10px 4px" }}>INITIATIVES</div>
          <div style={{ overflowY: "auto", flex: 1, padding: "2px 8px 10px" }}>
            {PODS.map(pod => (
              <div key={pod.id} data-podzone={pod.id} style={{ marginBottom: 8, minHeight: 22 }}>
                <div className="hov" style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${pod.color}44`, marginBottom: 3 }}>
                  <span style={{ flex: 1, font: "700 9px 'Barlow Condensed'", letterSpacing: ".08em", color: pod.color, padding: "3px 2px 2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pod.name.toUpperCase()}</span>
                  <Hamburger title={`Edit team ${pod.name}`} onClick={() => setEdit({ kind: "team", mode: "edit", team: pod.id })} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {(displayOrg[pod.id] || []).map((w, i) => (
                    <React.Fragment key={w}>
                      {dragWs?.active && wsDrop?.pod === pod.id && wsDrop.index === i && <div style={{ height: 2, background: "#E8A33D" }} />}
                      <div className="hov" style={{ position: "relative" }}>
                      <button className="wschip" title={w} data-wschip data-pod={pod.id} data-idx={i}
                        data-wsi={wsNum[w]}
                        onMouseEnter={() => !dragWs && hoverWs(w)}
                        onMouseLeave={() => hoverWs(null)}
                        onPointerDown={e => { e.preventDefault(); setDragWs({ ws: w, x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, active: false }); }}
                        style={{ background: wsColor[w], width: "100%", textAlign: "left", paddingRight: 22, overflow: "hidden", textOverflow: "ellipsis", cursor: "grab", opacity: dragWs?.active && dragWs.ws === w ? 0.35 : 1, outline: openWs === w ? "2px solid #E8A33D" : undefined }}>
                        {w}
                      </button>
                      <span style={{ position: "absolute", right: 1, top: 1 }}>
                        <Hamburger title={`Edit initiative ${w}`} onClick={() => setEdit({ kind: "initiative", mode: "edit", name: w, team: pod.id })} />
                      </span>
                      </div>
                    </React.Fragment>
                  ))}
                  {dragWs?.active && wsDrop?.pod === pod.id && wsDrop.index === (displayOrg[pod.id] || []).length && <div style={{ height: 2, background: "#E8A33D" }} />}
                  <Plus onClick={() => setEdit({ kind: "initiative", mode: "add", team: pod.id })}>Initiative</Plus>
                </div>
              </div>
            ))}
            <Plus style={{ width: "100%" }} onClick={() => setEdit({ kind: "team", mode: "add" })}>Team</Plus>
          </div>
        </div>}
        {!mobile && openWs && initiatives.includes(openWs) && (
          <div style={{ width: 330, flexShrink: 0, borderRight: "1px solid #364356", background: "#1B222C", overflowY: "auto", padding: "10px 12px" }}
            onMouseLeave={() => { setHighlight(null); setHighlightTask(null); }}>
            <div className="hov" style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6, position: "sticky", top: -10, background: "#1B222C", padding: "4px 0", zIndex: 2 }}>
              <span style={{ font: "700 14px 'Barlow Condensed'", letterSpacing: ".07em", color: wsColor[openWs], filter: "brightness(1.5)" }}>{openWs.toUpperCase()}</span>
              <Hamburger title={`Edit initiative ${openWs}`} onClick={() => setEdit({ kind: "initiative", mode: "edit", name: openWs, team: wsOfTeam(openWs) })} />
              <div style={{ flex: 1 }} />
              <button className="btn" onClick={() => { setOpenWs(null); setHighlight(null); }}>✕</button>
            </div>
            <div style={{ fontSize: 10, color: "#5F6B7C", marginBottom: 8 }}>hover to locate · click to fly there · ☰ to edit</div>
            {flyEpics.map(ep => (
              <div key={ep.name} style={{ marginBottom: 8 }}>
                <div className="hov flyep" data-flyep={ep.name} style={{ fontSize: 12, fontWeight: 700, color: highlightTask === ep.name ? "#E8A33D" : ep.inScope ? "#E8EDF3" : "#8A97A8", padding: "2px 0 2px 8px", cursor: "default", display: "flex", alignItems: "center", gap: 5 }}
                  onMouseEnter={() => setHighlightTask(ep.name)}
                  onMouseLeave={() => setHighlightTask(t => (t === ep.name ? null : t))}>
                  <PriIcon p={ep.pri} size={11} model={m} />
                  <span style={{ flex: 1 }}>{ep.name}{!ep.inScope && <span style={{ fontWeight: 400, fontSize: 10, color: "#5F6B7C" }}> · out of scope</span>}</span>
                  {ep.item && <Hamburger title={`Edit epic ${ep.name}`} onClick={() => setEdit({ kind: "epic", mode: "edit", epic: ep.name })} />}
                </div>
                {[...ep.entries].sort((a, b) => a.startDay - b.startDay).map(e => (
                  <div key={e.segId} className="flyrow hov" data-flyrow={e.segId}
                    style={flyoutFocus === e.segId ? { background: "#3A2F17", outline: "1px solid #E8A33D" } : undefined}
                    onMouseEnter={() => { setHighlight(e.segId); setRowHover(e.segId); }}
                    onMouseLeave={() => setRowHover(h => (h === e.segId ? null : h))}
                    onClick={() => { setPx(p => Math.max(p, 10)); setScrollReq(e.segId); }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: wsColor[openWs], flexShrink: 0 }} />
                    <span style={{ color: "#AEB9C8", width: 84, display: "flex", alignItems: "center", gap: 3 }}>
                      {discLabel(e.disc)}{e.num ? " " + e.num : ""}{e.isSub && <span title="linked to main chunk" style={{ fontSize: 9, opacity: .6 }}>🔗</span>}
                    </span>
                    <span style={{ color: "#8A97A8", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{labelOf(e.person)}{e.pinned ? " 📌" : ""}</span>
                    {rowHover === e.segId && e.isSub && <>
                      <button className="btn" style={{ padding: "0 5px", fontSize: 11 }} onClick={ev => { ev.stopPropagation(); bumpChunk(e.segId, -1); }}>−</button>
                      <button className="btn" style={{ padding: "0 5px", fontSize: 11 }} onClick={ev => { ev.stopPropagation(); bumpChunk(e.segId, 1); }}>+</button>
                      <span title="delete chunk (time returns to main)" style={{ cursor: "pointer", fontSize: 11 }} onClick={ev => { ev.stopPropagation(); delChunk(e.segId); }}>🗑</span>
                    </>}
                    {rowHover === e.segId && !e.isSub &&
                      <button className="btn" style={{ padding: "0 6px", fontSize: 10, borderColor: "#E8A33D", color: "#E8A33D" }} title="split a new linked chunk off this one (pulls 1pd)"
                        onClick={ev => { ev.stopPropagation(); addChunk(e.segId); }}>+CHUNK</button>}
                    <span style={{ color: "#5F6B7C", minWidth: 74, textAlign: "right" }}>{e.pd.toFixed(1)}pd · W{Math.floor(e.startDay / DPW) + 1}</span>
                    {ep.item && <Hamburger title={`Edit ${discLabel(e.disc)} task`} onClick={() => setEdit({
                      kind: "task", mode: "edit", epic: ep.name, disc: e.disc, person: e.person, pd: e.pd, ws: openWs,
                      chunks: ep.entries.filter(x => x.disc === e.disc).length,
                    })} />}
                  </div>
                ))}
                {ep.item && <Plus style={{ marginLeft: 8, marginTop: 2 }} onClick={() => setEdit({ kind: "task", mode: "add", epic: ep.name, ws: openWs })}>Task</Plus>}
              </div>
            ))}
            {!flyEpics.length && <div style={{ fontSize: 12, color: "#8A97A8", margin: "6px 8px 10px" }}>No epics yet.</div>}
            <Plus style={{ width: "100%" }} onClick={() => setEdit({ kind: "epic", mode: "add", ws: openWs })}>Epic</Plus>
          </div>
        )}
      {/* board */}
      {(!mobile || tab === "board") && <div ref={boardRef} style={docMode ? { position: "relative", touchAction: "pan-x pan-y" } : { overflow: "auto", flex: 1, minWidth: 0, position: "relative", touchAction: "pan-x pan-y", overscrollBehavior: "contain" }}>
        <div style={{ minWidth: RAIL + maxDay * pxPerDay, position: "relative" }}>
          <div ref={hdrRef} style={{ position: "sticky", top: 0, zIndex: 20, background: "#151A21", display: "flex" }}>
            <div style={{ width: RAIL, flexShrink: 0, position: "sticky", left: stickL, background: "#151A21", zIndex: 6, borderRight: "1px solid #2E3947", borderBottom: "1px solid #2E3947" }} />
            <div>
            <div style={{ display: "flex" }}>
              {Array.from({ length: Math.ceil(maxWeeks / MW) }, (_, b) => (
                <div key={b} onClick={() => setMsDetail(b)} title="Click for milestone detail"
                  style={{ width: weekPx * Math.min(MW, maxWeeks - b * MW), flexShrink: 0, boxSizing: "border-box", background: b % 2 ? "#1E2733" : "#232E3D", borderRight: "1px solid #2E3947", padding: "3px 6px", font: "600 11px 'Barlow Condensed'", letterSpacing: ".05em", color: "#E8A33D", cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden" }}>
                  {weekPx * MW >= 24 ? `M${b + 1}` : ""}{weekPx * MW > 60 && <span style={{ color: "#8A97A8" }}> · {fmt(dayToDate(Math.min(maxDay, (b + 1) * MW * DPW) - 1))}</span>}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", borderBottom: "1px solid #2E3947", position: "relative" }}>
              <div style={{ position: "absolute", left: nowDay * pxPerDay - 16, top: 0, zIndex: 5, background: "#5FD3BC", color: "#0d1418", font: "700 8px 'Barlow Condensed'", letterSpacing: ".1em", padding: "1px 4px", borderRadius: 2, pointerEvents: "none" }}>NOW</div>
              {Array.from({ length: maxWeeks }, (_, w) => (
                <div key={w} style={{ width: weekPx, flexShrink: 0, boxSizing: "border-box", borderRight: "1px solid #232B36", fontSize: 9, color: w + 1 === RUNWAY_W ? "#fff" : w >= RUNWAY_W ? "#c98080" : "#5F6B7C", background: w + 1 === RUNWAY_W ? "#C0392B" : w >= RUNWAY_W ? "#3A2426" : "transparent", padding: "1px 3px", whiteSpace: "nowrap", overflow: "hidden" }}>
                  {weekPx >= 18 ? `W${w + 1}` : ""}{pxPerDay > 5 ? " " + fmt(dayToDate(w * DPW)) : ""}
                  {dayView && <div style={{ display: "flex" }}>{DOW.map((d, i) => <span key={i} style={{ width: pxPerDay, fontSize: pxPerDay >= 60 ? 10 : 8, color: pxPerDay >= 60 ? "#8A97A8" : "#4A5568" }}>{d}{pxPerDay >= 60 ? " " + dayToDate(w * DPW + i).getDate() : ""}</span>)}</div>}
                </div>
              ))}
            </div>
            </div>
          </div>

          {(mobile ? PODS.filter(p => !hiddenPods.includes(p.id)) : PODS).map(pod => byWs ? (
            <div key={pod.id}>
              {teamHeader(pod)}
              {(displayOrg[pod.id] || []).map(w => {
                const L = wsLanes[w] || { items: [], lanes: 0 };
                const totalH = Math.max(minRowH, Math.max(1, L.lanes) * rowH);
                const people = new Set(L.items.map(it => it.nm)).size;
                const pd = L.items.reduce((a, it) => a + it.s.pd, 0);
                const abbr = (w.match(/[A-Za-z0-9]+/g) || [w]).slice(0, 2).map(x => x[0]).join("").toUpperCase() || "?";
                return (
                  <div key={pod.id + "\u0000" + w} style={{ display: "flex", height: totalH, borderBottom: "1px solid #1E2530", transition: "height .15s" }}>
                    {mobile ? (
                    <div style={{ width: RAIL, flexShrink: 0, position: "sticky", left: stickL, background: "#181F28", zIndex: 10, borderRight: "1px solid #2E3947", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: Math.max(3, (Math.min(rowH, totalH) - (smallAvatar ? 24 : 32)) / 2), paddingBottom: 3 }}>
                      <div role="button" title={w} onClick={() => { setBacklogOpen(w); setTab("backlog"); }}
                        style={{ position: "sticky", top: hdrH + 6, width: smallAvatar ? 24 : 32, height: smallAvatar ? 24 : 32, borderRadius: 7, background: wsColor[w], color: "#1a1d21", display: "grid", placeItems: "center", font: `700 ${smallAvatar ? 10 : 13}px/1 'Barlow Condensed'`, letterSpacing: ".04em", cursor: "pointer" }}>{abbr}</div>
                    </div>
                    ) : (
                    <div className="hov" onMouseEnter={() => hoverWs(w)} onMouseLeave={() => hoverWs(null)}
                      style={{ width: RAIL, boxSizing: "border-box", flexShrink: 0, padding: "4px 4px 3px 8px", position: "sticky", left: 0, background: "#181F28", zIndex: 10, borderRight: "1px solid #2E3947", display: "flex", gap: 7, alignItems: "flex-start" }}>
                      <span style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: wsColor[w], flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => { setOpenWs(o => (o === w ? null : w)); setHighlight(null); }}>
                        <div title={w} style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: openWs === w ? "#E8A33D" : "#E8EDF3" }}>{w}</div>
                        <div style={{ fontSize: 9, color: "#5F6B7C", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{L.items.length ? `${people} ${people === 1 ? "person" : "people"} · ${pd.toFixed(0)}pd` : "nothing scheduled"}</div>
                      </div>
                      <Hamburger title={`Edit initiative ${w}`} onClick={() => setEdit({ kind: "initiative", mode: "edit", name: w, team: pod.id })} />
                    </div>
                    )}
                    <div style={{ position: "relative", flex: 1, backgroundImage: gridBg }}>
                      <div style={{ position: "absolute", left: RUNWAY_W * weekPx, top: 0, bottom: 0, width: 2, background: "#C0392B", opacity: .6, zIndex: 2 }} />
                      <div style={{ position: "absolute", left: nowDay * pxPerDay, top: 0, bottom: 0, width: 2, background: "#5FD3BC", opacity: .75, zIndex: 3, pointerEvents: "none" }} />
                      {L.lanes > 1 && Array.from({ length: L.lanes - 1 }, (_, i) => (
                        <div key={i} style={{ position: "absolute", left: 0, right: 0, top: (i + 1) * rowH, height: 1, background: "#232B36" }} />
                      ))}
                      {L.items.map(it => renderBrick(it.nm, it.s, it.lane, undefined, true))}
                    </div>
                  </div>
                );
              })}
              <div style={{ display: "flex", borderBottom: "1px solid #1E2530" }}>
                <div style={{ width: mobile ? "auto" : RAIL, padding: "3px 8px", position: "sticky", left: stickL, background: "#181F28", zIndex: 10, borderRight: "1px solid #2E3947" }}>
                  <Plus style={mobile ? { padding: "6px 10px" } : undefined} onClick={() => setEdit({ kind: "initiative", mode: "add", team: pod.id })}>Initiative</Plus>
                </div>
              </div>
            </div>
          ) : (
            <div key={pod.id}>
              {teamHeader(pod)}
              {pod.members.map(nm => {
                const lanes = laneCount(nm);
                const showPreviewStrip = drag?.active && P[nm].disc === drag.seg.disc;
                const shared = !mobile && !smallAvatar && (PERSON_PODS[nm] || []).length > 1; // their rail carries an extra "also in" line
                const totalH = Math.max(shared ? 44 : minRowH, lanes * rowH + (showPreviewStrip ? 16 : 0));
                const name = labelOf(nm);
                return (
                  <div key={pod.id + "\u0000" + nm} data-person={nm}
                    style={{ display: "flex", height: totalH, borderBottom: "1px solid #1E2530", background: hoverTarget?.person === nm && drag?.active ? "#20303F" : "transparent", transition: "height .15s" }}>
                    {mobile ? (
                    <div style={{ width: RAIL, flexShrink: 0, position: "sticky", left: stickL, background: "#181F28", zIndex: 10, borderRight: "1px solid #2E3947", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: Math.max(3, (Math.min(rowH, totalH) - (smallAvatar ? 24 : 32)) / 2), paddingBottom: 3 }}>
                      <div style={{ position: "sticky", top: hdrH + 6 }}>
                        <Avatar name={P[nm].full} disc={discMeta(config, P[nm].disc)} size={smallAvatar ? 24 : 32} selected={panel?.kind === "person" && panel.nm === nm}
                          title={`${P[nm].full} · ${discLabel(P[nm].disc)}`} onClick={() => setPanel({ kind: "person", nm })} />
                      </div>
                    </div>
                    ) : (
                    <div className="hov" style={{ width: RAIL, boxSizing: "border-box", flexShrink: 0, padding: "3px 4px 3px 8px", position: "sticky", left: 0, background: "#181F28", zIndex: 10, borderRight: "1px solid #2E3947", display: "flex", gap: 7, alignItems: "flex-start" }}>
                      <span style={{ paddingTop: 3 }}><Avatar name={P[nm].full} disc={discMeta(config, P[nm].disc)} size={26} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span title={P[nm].userId ? `${name} · linked to a profile` : name} style={{ flex: 1, fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: !drag || P[nm].disc === drag.seg.disc ? "#E8EDF3" : "#4A5568" }}>{name}</span>
                        <input type="number" min={10} max={100} step={5} key={nm + ":" + Math.round(uOf(nm) * 100)} defaultValue={Math.round(uOf(nm) * 100)}
                          title={`${name}'s utilization: scheduled fraction of a ${DPW}-day week`}
                          onBlur={e => { const v = (+e.target.value || defU(nm) * 100) / 100; if (Math.abs(v - uOf(nm)) > 1e-6) setPersonUtil(nm, v); }}
                          onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          onPointerDown={e => e.stopPropagation()}
                          style={{ width: 34, background: "#232B36", border: "1px solid #364356", color: Math.abs(uOf(nm) - defU(nm)) < 0.001 ? "#8A97A8" : "#E8A33D", borderRadius: 3, padding: "1px 3px", font: "600 10px 'Barlow Condensed'", textAlign: "right" }} />
                        <span style={{ fontSize: 9, color: "#5F6B7C" }}>%</span>
                        <Hamburger title={`Edit ${name}`} onClick={() => setEdit({ kind: "person", mode: "edit", person: nm })} />
                      </div>
                      <div style={{ fontSize: 9, color: "#5F6B7C", fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{discLabel(P[nm].disc)}{P[nm].userId ? " · 👤 linked" : ""}</div>
                      {(() => {
                        // one person can sit in several teams; their row repeats in each, with the other teams' work dimmed
                        const also = (PERSON_PODS[nm] || []).filter(id => id !== pod.id).map(id => PODS.find(p => p.id === id)).filter(Boolean);
                        return shared && also.length ? <div title={`${name} is one person shared with ${also.map(p => p.name).join(", ")}. Dimmed bricks are that team's work.`}
                          style={{ fontSize: 9, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#8A97A8" }}>
                          ⇄ also in {also.map((p, i) => <span key={p.id} style={{ color: p.color }}>{i ? ", " : ""}{p.name.split(" · ")[0]}</span>)}
                        </div> : null;
                      })()}
                      </div>
                    </div>
                    )}
                    <div style={{ position: "relative", flex: 1, backgroundImage: gridBg }}>
                      {P[nm].offset ? <div style={{ position: "absolute", left: 0, width: P[nm].offset * weekPx, top: 0, bottom: 0, boxSizing: "border-box", background: "repeating-linear-gradient(45deg,#1E2530,#1E2530 6px,#232B36 6px,#232B36 12px)", color: "#5F6B7C", fontSize: 9, padding: "4px 6px", zIndex: 2, overflow: "hidden", whiteSpace: "nowrap" }}>Blocked until {P[nm].blockLabel || `W${P[nm].offset + 1}`}</div> : null}
                      <div style={{ position: "absolute", left: RUNWAY_W * weekPx, top: 0, bottom: 0, width: 2, background: "#C0392B", opacity: .6, zIndex: 2 }} />
                      <div style={{ position: "absolute", left: nowDay * pxPerDay, top: 0, bottom: 0, width: 2, background: "#5FD3BC", opacity: .75, zIndex: 3, pointerEvents: "none" }} />
                      {lanes > 1 && Array.from({ length: lanes - 1 }, (_, i) => (
                        <div key={i} style={{ position: "absolute", left: 0, right: 0, top: (i + 1) * rowH, height: 1, background: "#2E3947" }} />
                      ))}
                      {renderSegs(nm, pod.id)}
                      {showPreviewStrip && (
                        <div style={{ position: "absolute", left: 0, right: 0, top: lanes * rowH, height: hoverTarget?.person === nm && hoverTarget?.isNew ? rowH : 14, boxSizing: "border-box", background: hoverTarget?.person === nm && hoverTarget?.isNew ? "rgba(232,163,61,.12)" : "rgba(232,163,61,.05)", borderTop: "1px dashed #E8A33D66", color: "#E8A33D99", fontSize: 9, padding: "1px 6px", transition: "height .12s" }}>
                          {hoverTarget?.person === nm && hoverTarget?.isNew ? `drop for lane ${lanes + 1} — parallel work splits ${name.split(" ")[0]}'s capacity` : `+ lane ${lanes + 1}`}
                        </div>
                      )}
                      {hoverTarget?.person === nm && drag?.active && !hoverTarget.isNew && <div style={{ position: "absolute", left: hoverTarget.day * pxPerDay, top: hoverTarget.lane * rowH, height: rowH, width: 2, background: "#E8A33D", zIndex: 6 }} />}
                    </div>
                  </div>
                );
              })}
              <div style={{ display: "flex", borderBottom: "1px solid #1E2530" }}>
                <div style={{ width: mobile ? "auto" : RAIL, padding: "3px 8px", position: "sticky", left: stickL, background: "#181F28", zIndex: 10, borderRight: "1px solid #2E3947" }}>
                  <Plus style={mobile ? { padding: "6px 10px" } : undefined} onClick={() => setEdit({ kind: "person", mode: "add", team: pod.id })}>Person</Plus>
                </div>
              </div>
            </div>
          ))}
        </div>

        {dragWs?.active && <div style={{ position: "fixed", left: dragWs.x + 8, top: dragWs.y + 8, pointerEvents: "none", zIndex: 100, background: wsColor[dragWs.ws], color: "#1a1d21", padding: "3px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700, boxShadow: "0 4px 14px rgba(0,0,0,.5)" }}>{dragWs.ws}</div>}
        {drag?.active && <div style={{ position: "fixed", left: drag.x + 8, top: drag.y + 8, pointerEvents: "none", zIndex: 100, background: wsColor[drag.seg.ws.trim()], color: "#1a1d21", padding: "3px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700, boxShadow: "0 4px 14px rgba(0,0,0,.5)" }}>
          {drag.seg.task} · {drag.seg.pd.toFixed(1)}pd → {hoverTarget ? `${labelOf(hoverTarget.person)}${hoverTarget.lane > 0 ? ` · lane ${hoverTarget.lane + 1}` : ""}` : "…same-discipline rows only"}
        </div>}
      </div>}
      </div>

      {mobile && !landscape && (docMode
        ? <ScreenLayer z={40}><div style={{ position: "fixed", left: 0, right: 0, bottom: 0 }}><MobileNav tab={tab} onTab={onTab} /></div></ScreenLayer>
        : <MobileNav tab={tab} onTab={onTab} />)}

      {mobile && panel?.kind === "person" && <PersonPanel nm={panel.nm} config={config} m={m} uOf={uOf} setUtil={setPersonUtil} resolved={resolved}
        nowDay={nowDay} wsColor={wsColor} landscape={landscape} onClose={() => setPanel(null)}
        onEdit={() => { setEdit({ kind: "person", mode: "edit", person: panel.nm }); setPanel(null); }} />}
      {mobile && panel?.kind === "brick" && <BrickPanel nm={panel.nm} segId={panel.segId} config={config} m={m} resolved={resolved} wsColor={wsColor} state={state}
        landscape={landscape} onClose={() => setPanel(null)}
        onPin={seg => togglePin(panel.nm, seg)}
        onOpenInitiative={w => { setBacklogOpen(w); setTab("backlog"); setPanel(null); }}
        onEdit={(seg, pd) => {
          const entries = wsIndex[seg.ws.trim()]?.[seg.task] || [];
          setEdit({ kind: "task", mode: "edit", epic: seg.task, disc: seg.disc, person: panel.nm, pd, ws: seg.ws.trim(), chunks: entries.filter(x => x.disc === seg.disc).length });
          setPanel(null);
        }} />}

      {pinPrompt && <div style={{ position: "fixed", bottom: mobile && !landscape ? 90 : 20, maxWidth: "94vw", flexWrap: "wrap", left: "50%", transform: "translateX(-50%)", background: "#232B36", border: "1px solid #E8A33D", borderRadius: 6, padding: "8px 14px", zIndex: 100, display: "flex", gap: 10, alignItems: "center", fontSize: 13 }}>
        Moved <b>{pinPrompt.seg.task}</b> to {labelOf(pinPrompt.person)}.
        <button className="btn" style={{ borderColor: "#E8A33D", color: "#E8A33D" }} onClick={() => {
          const segNow = (resolved[pinPrompt.person] || []).find(s => baseIdOf(s.id) === pinPrompt.seg.id && s.lane === 0);
          if (segNow) togglePin(pinPrompt.person, segNow);
        }}>📌 Pin it</button>
        <span style={{ color: "#5F6B7C", fontSize: 11 }}>pinned work stays put when other work shifts</span>
      </div>}

      {msDetail !== null && <div onClick={() => setMsDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: "#1B222C", border: "1px solid #364356", borderRadius: 8, padding: 20, maxWidth: 720, maxHeight: "80vh", overflow: "auto", width: "90%" }}>
          <div style={{ font: "700 18px 'Barlow Condensed'", color: "#E8A33D", marginBottom: 10 }}>M{msDetail + 1} — ends {fmt(dayToDate(Math.min(maxDay, (msDetail + 1) * MW * DPW) - 1))} (live)</div>
          {PODS.map(pod => {
            const s = milestones[pod.idx]?.[msDetail];
            if (!s || (!s.done.length && !s.wip.length)) return null;
            return <div key={pod.id} style={{ marginBottom: 12 }}>
              <div style={{ font: "700 13px 'Barlow Condensed'", color: pod.color, letterSpacing: ".06em" }}>{pod.name}</div>
              {s.done.length > 0 && <div style={{ fontSize: 12.5, margin: "3px 0" }}><span style={{ color: "#7BBF7B", fontWeight: 700 }}>DONE</span> {s.done.join("; ")}</div>}
              {s.wip.length > 0 && <div style={{ fontSize: 12.5, color: "#AEB9C8" }}><span style={{ color: "#E8A33D", fontWeight: 700 }}>WIP</span> {s.wip.map(([t, p]) => `${t} (${p}%)`).join("; ")}</div>}
            </div>;
          })}
          <button className="btn" onClick={() => setMsDetail(null)}>Close</button>
        </div>
      </div>}

      {edit && (() => {
        const dlg = <EditDialog key={JSON.stringify(edit)} edit={edit} onClose={() => setEdit(null)}
          ctx={{ config, state, m, resolved, act, saveConfig, displayOrg, project, wsOfTeam, initiatives,
            afterInitiative: (w, prev) => setOpenWs(o => (prev === undefined || o === prev ? w : o)) }} />;
        return mobile ? <ScreenLayer z={250}>{dlg}</ScreenLayer> : dlg;
      })()}

      {!mobile && <div style={{ padding: "4px 14px", borderTop: "1px solid #2E3947", fontSize: 10.5, color: "#5F6B7C", display: "flex", gap: 14, flexWrap: "wrap" }}>
        <span>📌 = pinned (click to unpin; stays put when other work shifts)</span><span>dashed = out-of-budget (N)</span><span>faint = other pod lane</span><span>⚠ = not intended dev</span><span>drag below a row = new parallel lane (splits capacity; rows 2+ free-float where dropped, row 1 has gravity)</span><span>red line = W{RUNWAY_W} runway</span><span>pinch or Ctrl+scroll to zoom</span>
      </div>}
    </div>
  );
}

export default Board;
